import React, { Component } from 'react';
import {Link} from 'react-router-dom';

import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocUriEntity extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENRICH)
        }) 
    }

    render() {
        return (
            <div className="bucket" id="top">
                <div>
                    <h3 id="uri" className="heading-1">Uri</h3>
                    <div className="description">
                        The extraction of a uniform resource identifier (URI) is based upon {miscUtils.createExternalLink('https://www.ietf.org/rfc/rfc3986.txt','RFC 3986')} standards. The resource identifier must
                        be absolute (exclude relative references like //path/to/a/file) and requires a minimum of a host. Encoded URI's
                        are accepted and will attempt to be decoded. The attributes and normalizedText will represent the decoded URI.
                    </div>

                    <div className="description">
                        An extracted URI may be represented by the following attributes:
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">host</td>
                            <td className="meaning">Represents the host of the URI expressed as an IPv4 address or full registered name.</td>
                            <td className="type">String</td>
                            <td className="constraints">Mandatory</td>
                            <td className="notes">Must be a valid registered name as specified by {miscUtils.createExternalLink('http://www.iana.org/domains/root/db','IANA')}.
                                For ip addresses as the host, see the <Link to="/documentation/doc_ipv4">IPv4</Link> entity type.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">port</td>
                            <td className="meaning">Represents the port number of the extracted host.</td>
                            <td className="type">Number</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">scheme</td>
                            <td className="meaning">The scheme of the URI.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Must be a valid scheme as specified by the {miscUtils.createExternalLink('https://www.iana.org/assignments/uri-schemes/uri-schemes.xhtml','IANA')}. 
                                Ex. <i>http</i>, <i>https</i>, <i>mailto</i>.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">path</td>
                            <td className="meaning">The absolute path component of the URI.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                                Indicated by the presence of a <i>/</i>.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">query</td>
                            <td className="meaning">The query component of the URI.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                                Indicated by the presence of a <i>?</i>.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">fragment</td>
                            <td className="meaning">The fragment component of the URI.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                                Indicated by the presence of a <i>#</i>.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">user</td>
                            <td className="meaning">The auth user of the URI.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">password</td>
                            <td className="meaning">The password of the auth user.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">ipAddress</td>
                            <td className="meaning">The enriched results of the resolved IP address for the extracted host.</td>
                            <td className="type">JSONObject</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                                The IP enrichment feature must be enabled and configured for this attribute. See below.
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div className="description">
                        Externally routed addresses may be enriched with geospatial data if the feature is enabled and configured. For
                        URI, this enrichment is nested under an <i>ipAddress</i> attribute.
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>ipAddress Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">continentLabel</td>
                            <td className="meaning">The enclosing continent name of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">countryLabel</td>
                            <td className="meaning">The enclosing country name of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">countryIso</td>
                            <td className="meaning">The {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-1','ISO 3166-1')} country code for the country associated with the location.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">The two character {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-1','ISO 3166-1')} country code.</td>
                        </tr>
                        <tr>
                            <td className="attribute">admin1Label</td>
                            <td className="meaning">The enclosing first level region of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">admin1Iso</td>
                            <td className="meaning">The {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-2','ISO 3166-2')} code for the first level region associated with the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A string of up to 3 characters containing the region portion of the {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-2','ISO 3166-2')} code.</td>
                        </tr>
                        <tr>
                            <td className="attribute">cityLabel</td>
                            <td className="meaning">The city name associated with the location of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">postalCode</td>
                            <td className="meaning">The postal code associated with the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Available for some URI's in Australia, Canada, France, Germany, Italy, Spain, Switzerland, United Kingdom and the US.</td>
                        </tr>
                        <tr>
                            <td className="attribute">latitude</td>
                            <td className="meaning">The approximate latitude of the location associated with the network.</td>
                            <td className="type">Double</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Value is approximate.</td>
                        </tr>
                        <tr>
                            <td className="attribute">longitude</td>
                            <td className="meaning">The approximate longitude of the location associated with the network.</td>
                            <td className="type">Double</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Value is approximate.</td>
                        </tr>
                        <tr>
                            <td className="attribute">accuracyRadius</td>
                            <td className="meaning">The radius in kilometers around the specified location where the hostname is likely to be.</td>
                            <td className="type">Integer</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">metroCode</td>
                            <td className="meaning">The metro code associated with the hostname.</td>
                            <td className="type">Integer</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Only available for networks in the US.</td>
                        </tr>
                        <tr>
                            <td className="attribute">timeZone</td>
                            <td className="meaning">The time zone associated with the location of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Specified by the {miscUtils.createExternalLink('https://www.iana.org/time-zones','IANA Time Zone Database')}. Ex. "America/New_York".</td>
                        </tr>
                        <tr>
                            <td className="attribute">legitimateProxy</td>
                            <td className="meaning">Suggests whether the hostname is a virus based proxy and not a dedicated proxy server.</td>
                            <td className="type">Boolean</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">address</td>
                            <td className="meaning">The IPv4 or IPv6 network of the hostname.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <h4 className="heading-2">Examples</h4>
                    <div className="example-content">
                        <b>OnBroadway.com</b> sells tickets for many plays, musicals, and other shows.
                    </div>
                    <HttpRunner example="host1" 
                        query="enrich" 
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_URI1) : null}/>

                    <div className="example-content">
                        Full details of all certified products are available on the forum website at: <b>http://www.homegridforum.org/content/pages.php?pg=certified_systems</b> .
                    </div>
                    <HttpRunner example="host2" 
                        query="enrich" 
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_URI2) : null}/>
                </div>
            </div>
        );
    }
}

export default DocUriEntity;